import { all, call, getContext, put, select, setContext, takeEvery } from 'redux-saga/effects';

import logger from '@elab/core/utils/logger';
import { selectors as addressSelectors } from '@elab/core/store/address';

import { selectors as configSelectors } from '../../store/config';

import api from './api';
import {
    getCitiesFailedResponseErorrMessage,
    getDistrictsFailedResponseErorrMessage,
    getNeighborhoodsFailedResponseErorrMessage,
    normalizeAddressResponse,
} from './helpers';

import { getActions, getSelectors, getTypes } from '.';

export function* handleSetInitialData() {
    const shouldRequestCity = yield select(configSelectors.shouldRequestCityListOnInit);

    if (shouldRequestCity) {
        yield call(loadCities);
    }
}

export function* loadCities() {
    const { selectors, actions } = yield getContext('globalCtx');
    const { localActions } = yield getContext('localCtx');

    const cityField = yield select(selectors.getDataField, 'town');

    const shouldRequestCities =
        cityField && (!cityField.values || !cityField.values.length) && cityField.type === 'SELECT';

    if (shouldRequestCities) {
        try {
            yield put(actions.setDataFieldValue('town', '', true));
            yield put(actions.setDataFieldValue('district', '', true));
            yield put(localActions.setCityReferredToCurrentDistricts({ city: null }));

            const citiesResponse = yield call(api.getCities);
            const normalizedCities = normalizeAddressResponse(citiesResponse);

            yield put(actions.setDataFieldValues({ fieldName: 'town', values: normalizedCities }));
        } catch (e) {
            yield call(logger.error, getCitiesFailedResponseErorrMessage(), e);
        }
    }
}

export function* handleFieldChange({ payload }) {
    const { name, value, prefill } = payload;
    const isCityField = name === 'town';

    const isChangingCityAffectsDistricts = yield select(
        configSelectors.isChangingCityAffectsDistricts,
    );

    if (isCityField && isChangingCityAffectsDistricts) {
        yield call(loadDistricts, value, prefill);
    }
}

export function* handleDistrictFieldChange({ payload }) {
    const { name, value, prefill } = payload;
    const isDistrictField = name === 'district' || name === 'town';
    const useNeighborhood = yield select(configSelectors.useNeighborhood);
    if (isDistrictField && value && useNeighborhood) {
        yield call(loadNeighborhoods, value, prefill);
    }
}

export function* loadDistricts(city, prefill) {
    const { actions, selectors } = yield getContext('globalCtx');
    const { localActions, localSelectors } = yield getContext('localCtx');
    if (!city) {
        return;
    }

    let districts = [];
    const districtField = yield select(selectors.getDataField, 'district');

    const cityReferredToDistricts = yield select(localSelectors.getCityReferredToCurrentDistricts);

    const isCurrentCityEaqulToRefereedToDistricts =
        cityReferredToDistricts && cityReferredToDistricts === city;

    const shouldRequestDistricts =
        districtField &&
        districtField.type === 'SELECT' &&
        !isCurrentCityEaqulToRefereedToDistricts;

    if (!shouldRequestDistricts) {
        return;
    }

    districts = yield select(localSelectors.getDistrictsByCity, city);

    if (districts && districts.length) {
        yield put(actions.setDataFieldValues({ fieldName: 'district', values: districts }));
    } else {
        try {
            const districtsResponse = yield call(api.getDistricts, city.toUpperCase());
            districts = normalizeAddressResponse(districtsResponse);
            yield put(actions.setDataFieldValues({ fieldName: 'district', values: districts }));

            yield put(localActions.setCityReferredToCurrentDistricts({ city }));

            yield put(
                localActions.saveDistrictsByCity({
                    cityName: city,
                    districtList: districts,
                }),
            );
        } catch (e) {
            yield call(logger.error, getDistrictsFailedResponseErorrMessage(city), e);
        }
    }

    if (!prefill) {
        yield put(actions.setDataFieldValue('district', '', true));
        yield put(actions.setDataFieldValue('neighborhood', '', true));
        yield put(actions.setDataFieldValues({ fieldName: 'neighborhood', values: [] }));
    }
}

export function* loadNeighborhoods(district, prefill) {
    const { actions, selectors } = yield getContext('globalCtx');
    const { localActions, localSelectors } = yield getContext('localCtx');
    const cityField = yield select(selectors.getDataField, 'town');
    const districtField = yield select(selectors.getDataField, 'district');
    const city = cityField.value;
    district = districtField.value;
    if (!district || !city) {
        return;
    }

    let neighborhoods = [];
    const neighborhoodField = yield select(selectors.getDataField, 'neighborhood');
    const neighborhoodFieldType = yield select(configSelectors.neighborhoodFieldType);
    const districtReferredToNeighborhoods = yield select(
        localSelectors.districtReferredToCurrentNeighborhoods,
    );
    const isCurrentDistrictEqualToRefereedToNeighborhoods =
        districtReferredToNeighborhoods && districtReferredToNeighborhoods === district;

    const shouldRequestNeighborhoods =
        neighborhoodField &&
        neighborhoodField.type === neighborhoodFieldType &&
        !isCurrentDistrictEqualToRefereedToNeighborhoods;

    if (!shouldRequestNeighborhoods) {
        return;
    }

    neighborhoods = yield select(localSelectors.getNeighborhoodsByDistrict, district);
    if (neighborhoods && neighborhoods.length) {
        yield put(actions.setDataFieldValues({ fieldName: 'neighborhood', values: neighborhoods }));
    } else {
        const useNeighborhood = yield select(configSelectors.useNeighborhood);
        try {
            const neighborhoodsResponse = yield call(
                api.getNeighborhoods,
                city.toUpperCase(),
                district.toUpperCase(),
            );
            const neighborhoodFieldInfo = yield select(addressSelectors.getNeighborhoodFieldInfo);
            neighborhoodsResponse.neighborhoods = [
                ...neighborhoodsResponse.neighborhoods,
                neighborhoodFieldInfo,
            ];
            neighborhoods = normalizeAddressResponse(neighborhoodsResponse, useNeighborhood);
            yield put(
                actions.setDataFieldValues({ fieldName: 'neighborhood', values: neighborhoods }),
            );

            yield put(localActions.setDistrictReferredToCurrentNeighborhoods({ district }));

            yield put(
                localActions.saveNeighborhoodsByDistrict({
                    districtName: district,
                    neighborhoodList: neighborhoods,
                }),
            );
        } catch (e) {
            yield call(logger.error, getNeighborhoodsFailedResponseErorrMessage(district), e);
        }
    }
    if (!prefill) {
        yield put(actions.setDataFieldValue('neighborhood', '', true));
    }
}

export function* initAddressRelationsWatchers(globalCtx, storeName) {
    const { types } = globalCtx;

    const localTypes = getTypes(storeName);
    const localSelectors = getSelectors(storeName);
    const localActions = getActions(storeName);
    const localCtx = { localTypes, localActions, localSelectors, storeName };

    yield setContext({ globalCtx, localCtx });
    yield all([
        takeEvery(types.SET_DATA_FIELD_VALUE, handleFieldChange),
        takeEvery(types.SET_DATA_FIELD_VALUE, handleDistrictFieldChange),
        takeEvery(types.SET_INITIAL_DATA, handleSetInitialData),
    ]);
}
